import { useCallback, useMemo, useState } from 'react';
import { startOfWeek, getWeek, addWeeks, subWeeks, subMonths, addMonths } from 'date-fns';
import { CalendarCardWrapper, Cards, Dates, Header, Icon, LeftHeader, Main, RightHeader } from './styles';
import dayjs from 'dayjs';
import { createNewComment, updateTaskDetails } from '../../../../services/taskServices';
import Monthview from '../../../../component/monthView';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import { isEmpty } from '../../../../helpers/common';
import { useHotkeys } from 'react-hotkeys-hook';
import Modal from '../../../../component/modals/modal';
import DeleteModal from '../../../../component/modals/deleteModal';
import { captureException } from '../../../../services/logService';
import { rootStore } from '../../../../mobx/rootStore';
import WeekViewCard from '../../../../component/weekviewCard';
import Calendarviewcard from '../../../../component/calendarViewCard';
import { TaskModel } from '../../../../mobx/models/task';
import { DataCommentsInterface } from '../../../../interfaces/TaskInterface';
import { BUTTON_TYPE, COMMENT_TYPE, ICON_BUTTON_SIZE, ICON_BUTTON_TYPE } from '../../../../global/constants';
import { trackAnalyticActivity } from '../../../../services/analyticsService';
import { TASK_ANALYTICS } from '../../../../global/analyticsConstants';
import CreateTaskModalV2 from '../../../../component/modals/createTaskModal';
import Button from '../../../../component/button';
import IconButton from '../../../../component/button/iconButton';

interface Props {
  loadData: () => void;
  currentDate: Date;
}

function CalendarView(props: Props) {
  const { loadData, currentDate = new Date() } = props;

  // For display monthView functionality manage below state
  const [startDayOfWeek, setStartDayOfWeek] = useState(dayjs(startOfWeek(new Date(), { weekStartsOn: 1 })));
  const [selectedItem, setSelectedItem] = useState<TaskModel>();
  const [open, setOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [activeTaskId, setActiveTaskId] = useState<string>();
  //Mobx store variables
  const { projectStore } = rootStore;
  const { currentProject } = projectStore;
  const { weekWiseTasks } = currentProject || {};

  // For monthView variable manage static false as we have only week view for now
  const monthView = false;

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const activeTask = useMemo(() => {
    if (!activeTaskId) return undefined;

    return weekWiseTasks
      ?.flatMap((item) => [...(item.tasks || []), ...(item.completedTasks || [])])
      .find((task) => task.id === activeTaskId);
  }, [activeTaskId, weekWiseTasks]);

  const close = useCallback(() => {
    setOpen(false);
    rootStore.taskStore.createTaskInput.clear();
  }, []);

  const onClickToday = useCallback(() => {
    const startDayOfWeekDate = dayjs(startOfWeek(new Date(), { weekStartsOn: 1 }));
    setStartDayOfWeek(dayjs(startDayOfWeekDate));
    currentProject?.setStartOfTheWeek(startDayOfWeekDate.toDate());
  }, [currentProject]);

  const isCurrentWeek = useCallback((): boolean => {
    const startOfWeekDisplayed = dayjs(startDayOfWeek);
    const endOfWeekDisplayed = dayjs(startOfWeekDisplayed).add(6, 'days');
    const today = dayjs();
    return today.isAfter(startOfWeekDisplayed.subtract(1, 'day')) && today.isBefore(endOfWeekDisplayed.add(1, 'day'));
  }, [startDayOfWeek]);

  useHotkeys('c', () => {
    setTimeout(() => {
      setOpen(true);
      rootStore.taskStore.createTaskInput.setProject(currentProject?.id || '');
    }, 100);
  });

  const changeWeekHandle = (btnType: string, isMonthView: boolean) => {
    const currentMonth = currentDate;
    if (!isMonthView) {
      let updatedMonth, updatedWeek;
      if (btnType === 'prev') {
        updatedMonth = subWeeks(currentMonth, 1);
        updatedWeek = getWeek(subWeeks(currentMonth, 1));
      } else if (btnType === 'next') {
        updatedMonth = addWeeks(currentMonth, 1);
        updatedWeek = getWeek(addWeeks(currentMonth, 1));
      }
      if (updatedMonth && updatedWeek) {
        const startDayOfWeekDate = dayjs(startOfWeek(updatedMonth, { weekStartsOn: 1 }));
        setStartDayOfWeek(startDayOfWeekDate);
        currentProject?.setStartOfTheWeek(startDayOfWeekDate.toDate());
      }
    } else {
      let updatedMonth;
      if (btnType === 'prev') {
        updatedMonth = subMonths(currentMonth, 1);
      } else if (btnType === 'next') {
        updatedMonth = addMonths(currentMonth, 1);
      }
      if (updatedMonth) {
        const startDayOfWeekDate = dayjs(startOfWeek(updatedMonth, { weekStartsOn: 1 }));
        setStartDayOfWeek(startDayOfWeekDate);
        currentProject?.setStartOfTheWeek(startDayOfWeekDate.toDate());
      }
    }
  };

  const onClickEdit = useCallback(async (task: TaskModel) => {
    rootStore.taskStore.createTaskInput.updateWith(task);
    setOpen(true);
  }, []);

  const onClickClone = useCallback(async (task: TaskModel) => {
    rootStore.taskStore.createTaskInput.updateWith(task);
    rootStore.taskStore.createTaskInput.setDueDate(null);
    rootStore.taskStore.createTaskInput.setId(undefined);
    setOpen(true);
  }, []);

  const OpenModal = (item: TaskModel) => {
    setSelectedItem(item);
    setIsModalOpen(true);
  };

  const Cancel = () => {
    setIsModalOpen(false);
    setSelectedItem(undefined);
  };

  const deleteTask = useCallback(
    async (task: TaskModel) => {
      try {
        setDeleteLoading(true);
        const payloadTask = { isArchived: true, projectId: task?.projectId };
        const response = await updateTaskDetails(task?.id, payloadTask);
        setIsModalOpen(false);
        setSelectedItem(undefined);
        if (response) {
          loadData();
        }
      } catch (error) {
        captureException(error);
      } finally {
        setDeleteLoading(false);
      }
    },
    [loadData]
  );

  const handleDeleteConfirm = useCallback(() => {
    if (selectedItem) deleteTask(selectedItem);
  }, [selectedItem, deleteTask]);

  const renderDays = useCallback(() => {
    return weekWiseTasks?.map((item, i) => {
      return (
        <WeekViewCard
          key={i}
          dayName={item.dayName}
          date={item.date}
          loadData={() => {}}
          tasks={item.tasks || []}
          completedTasks={item.completedTasks || []}
          dateString={item.dateString}
          onClickEdit={onClickEdit}
          onClickClone={onClickClone}
          OpenModal={OpenModal}
          className={'desktop-calender-list'}
        />
      );
    });
  }, [onClickClone, onClickEdit, weekWiseTasks]);

  const onDragStart = useCallback((event: DragStartEvent) => {
    setActiveTaskId(event.active.id as string);
  }, []);

  const handleDragCancel = useCallback(() => setActiveTaskId(undefined), []);

  const onDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveTaskId(undefined);
      if (!over || !weekWiseTasks) return;

      const taskId = active.id as string;
      const sourceDueDate = active.data?.current?.dateString as string | undefined;
      // over could be a droppable (day column) or another draggable (task) — both carry dateString
      const destinationDueDate = over.data?.current?.dateString as string | undefined;

      if (!destinationDueDate || !sourceDueDate || sourceDueDate === destinationDueDate) return;

      const dayIndex = weekWiseTasks?.findIndex((item) => item?.dateString === sourceDueDate);
      if (dayIndex === -1) return;
      const day = weekWiseTasks[dayIndex];
      const taskIndex = day?.tasks?.findIndex((item) => item?.id === taskId);
      if (taskIndex === -1) return;
      const task = day?.tasks[taskIndex];
      const updatedDate = dayjs(destinationDueDate).utc().format();
      task.setDueDate(new Date(destinationDueDate));
      const payload = {
        previousDueDate: task?.dueDate,
        dueDate: updatedDate
      };
      const payloadTask = { dueDate: payload?.dueDate, projectId: task?.projectId };
      const response = await updateTaskDetails(task?.id, payloadTask);
      const commentPayload: DataCommentsInterface = {
        Type: COMMENT_TYPE.SET_DUE_DATE,
        DueDate: updatedDate
      };
      if (!isEmpty(payload?.previousDueDate)) {
        commentPayload['Type'] = COMMENT_TYPE.CHANGE_DUE_DATE;
        commentPayload['PreviousDueDate'] = payload?.previousDueDate?.toISOString();
      }
      currentProject?.setStartOfTheWeek(dayjs(destinationDueDate).toDate());
      const resultRes = await createNewComment(task?.id, commentPayload);
      if (response && resultRes) {
        if (commentPayload.Type === COMMENT_TYPE.SET_DUE_DATE) trackAnalyticActivity(TASK_ANALYTICS.SCHEDULED);
        else trackAnalyticActivity(TASK_ANALYTICS.RESCHEDULED);
      }
    },
    [weekWiseTasks, currentProject]
  );

  return (
    <>
      <Main>
        <Header>
          <LeftHeader>
            {/* Week view */}
            {monthView ? (
              <Dates>{dayjs(currentDate).format('MMM')} 2024</Dates>
            ) : (
              <Dates>
                {startDayOfWeek.format('DD MMM')} - {dayjs(startDayOfWeek).add(6, 'days').format('DD MMM')},{' '}
                {currentDate?.getFullYear()}
              </Dates>
            )}
            {/* Month view */}
            <Icon>
              <IconButton
                iconName='new-back-arrow-icon'
                size={ICON_BUTTON_SIZE.MEDIUM}
                variant={ICON_BUTTON_TYPE.TERTIARY}
                onClick={() => changeWeekHandle('prev', monthView)}
              />
              <IconButton
                iconName='updated-right-arrow-icon'
                size={ICON_BUTTON_SIZE.MEDIUM}
                variant={ICON_BUTTON_TYPE.TERTIARY}
                onClick={() => changeWeekHandle('next', monthView)}
              />
            </Icon>
          </LeftHeader>
          <RightHeader>
            {!isCurrentWeek() && (
              <Button title='Today' onClick={onClickToday} variant={BUTTON_TYPE.SECONDARY} size='small' />
            )}
          </RightHeader>
        </Header>
        {/* Week view and Month view */}
        {monthView ? (
          <Monthview currentMonth={currentDate} monthData={[]} />
        ) : (
          <DndContext
            sensors={sensors}
            onDragStart={onDragStart}
            onDragCancel={handleDragCancel}
            onDragEnd={onDragEnd}>
            <Cards>{renderDays()}</Cards>
            <DragOverlay>
              {activeTask ? (
                <CalendarCardWrapper>
                  <Calendarviewcard
                    loadData={loadData}
                    taskDetails={activeTask}
                    currentProject={activeTask.project || undefined}
                  />
                </CalendarCardWrapper>
              ) : null}
            </DragOverlay>
          </DndContext>
        )}
        <Modal open={open} onClose={close} isCreateTask={true} maxWidth={'680px'}>
          <CreateTaskModalV2 onCancel={close} context={{ source: 'shortcut' }} />
        </Modal>
        <Modal open={isModalOpen} onClose={Cancel} maxWidth={'334px'}>
          <DeleteModal
            onClose={Cancel}
            onOk={handleDeleteConfirm}
            loading={deleteLoading}
            modalTitle='Delete Task?'
            description='Are you sure you want to delete this task?'
          />
        </Modal>
      </Main>
    </>
  );
}

export default CalendarView;
